import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Checkbox, Text, yup } from '@app/formik';
import { Modal } from '@app/ui/modal';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { MATCHING_RULE_UPSERT } from '../../gql';
import {
  MatchingDefinitions,
  MatchingRule,
  MatchingRuleKind,
  MatchingRuleUpsertInput,
  RuleField,
  RuleLogic,
  RuleType,
} from '../../types';
import {
  MATCHING_GRADE_VALUES,
  MATCHING_RULE_KIND_VALUES,
  MATCHING_RULE_TYPE_VALUES,
  LOGIC_VALUES,
} from '../../constants';
import {
  emptyMatchingConditionRow,
  emptyMatchingGradeGroupRow,
  emptyMatchingSourceRow,
  getGraphQLErrorMessage,
  MatchingGradeGroupFormValue,
  matchingSourceRowError,
  MatchingSourceFormValue,
  toMatchingGradeGroupInput,
  toMatchingGradeGroupRow,
  toMatchingSourceInput,
  toMatchingSourceRow,
} from '../../utils';
import { MatchingSourcePicker } from './MatchingSourcePicker';
import { MatchingConditionRow } from './MatchingConditionRow';

export interface MatchingRuleEditorProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly ruleSetVersionId: number;
  readonly rule: MatchingRule | null;
  // The section a NEW rule was added from (exclusion/blocking/compatibility "Add rule" button) —
  // ignored once `rule` is set.
  readonly initialKind?: MatchingRuleKind;
  readonly fields: RuleField[];
  // Every OTHER draft rule, for the `pairsWith` picker (V-5: cross-kind, symmetric, never self).
  readonly allRules: MatchingRule[];
  readonly onSaved: () => Promise<unknown> | void;
}

interface FormValues {
  kind: MatchingRuleKind;
  type: RuleType;
  name: string;
  ruleText: string;
  weight: string;
  enabled: boolean;
  pairsWith: string;
  sources: MatchingSourceFormValue[];
  definitions: MatchingDefinitions;
  grades: MatchingGradeGroupFormValue[];
}

// `Text` (`@app/formik`) spreads `{...props}` BEFORE binding its own `onChange`, so a caller
// `onChange` on the `kind` select would be silently discarded (forms-formik-yup.md) — a headless
// `useUpdateEffect` child is the house pattern instead (mirrors `<AutoSave/>`'s shape). Skips the
// initial mount, so opening the editor never wipes an existing rule's `pairsWith`/`definitions`.
const ResetOnKindChange = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useUpdateEffect(() => {
    setFieldValue('pairsWith', '');
    setFieldValue('definitions', {});
    setFieldValue('grades', []);
  }, [values.kind]);

  return null;
};

const OTHER_KIND: Record<MatchingRuleKind, MatchingRuleKind | null> = {
  [MatchingRuleKind.exclusion]: null,
  [MatchingRuleKind.blocking]: MatchingRuleKind.compatibility,
  [MatchingRuleKind.compatibility]: MatchingRuleKind.blocking,
};

const buildInitialValues = (rule: MatchingRule | null, initialKind: MatchingRuleKind): FormValues => {
  if (!rule) {
    return {
      kind: initialKind,
      type: RuleType.ai,
      name: '',
      ruleText: '',
      weight: '',
      enabled: true,
      pairsWith: '',
      sources: [emptyMatchingSourceRow()],
      definitions: {},
      grades: [],
    };
  }

  return {
    kind: rule.kind,
    type: rule.type,
    name: rule.name,
    ruleText: rule.ruleText,
    weight: rule.weight != null ? String(rule.weight) : '',
    enabled: rule.enabled,
    pairsWith: rule.pairsWith ?? '',
    sources: rule.sources.length ? rule.sources.map(toMatchingSourceRow) : [emptyMatchingSourceRow()],
    definitions: rule.definitions ?? {},
    grades: rule.grades.length ? rule.grades.map(toMatchingGradeGroupRow) : [],
  };
};

// One form for CREATE and EDIT, three kinds (SPEC §6.3): exclusion (no weight, `excluded`
// definition, no `pairsWith`), blocking (+ weight, `blocking` definition, `pairsWith` from the
// compatibility kind), compatibility (+ weight, three definitions, `pairsWith` from the blocking
// kind). `type: exact` additionally offers ordered grade/condition groups. Explicit Save dialog, no
// `<AutoSave/>` — same reasoning as `ColorScoringRuleEditor` (opened/closed in place, every sibling
// dialog already uses this shape).
export const MatchingRuleEditor = ({
  open,
  onClose,
  ruleSetVersionId,
  rule,
  initialKind = MatchingRuleKind.compatibility,
  fields,
  allRules,
  onSaved,
}: MatchingRuleEditorProps) => {
  const { t } = useTranslation('common');
  const translate = (key: string) => t(key) ?? key;

  const fieldsByEntityAndPath = useMemo(
    () => new Map(fields.map(field => [`${field.entity}:${field.path}`, field])),
    [fields],
  );

  const [upsertRule, { loading }] = useMutation<{ matchingRuleUpsert: MatchingRule }>(MATCHING_RULE_UPSERT);

  const initialValues = useMemo(() => buildInitialValues(rule, initialKind), [rule?.id]);

  const pairsWithOptions = useMemo(
    () => (rule ? allRules.filter(candidate => candidate.id !== rule.id) : allRules),
    [allRules, rule?.id],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(),
        ruleText: yup.string().required(),
        weight: yup.string().when('kind', {
          is: (kind: MatchingRuleKind) => kind !== MatchingRuleKind.exclusion,
          then: yup
            .string()
            .test(
              'isWeight',
              translate('rules.matching.validation.weightRequired'),
              value => !!value && Number.isInteger(Number(value)) && Number(value) >= 0,
            ),
        }),
        sources: yup.array().test({
          name: 'matchingSourceRows',
          test(rows, context) {
            const sourceRows = (rows ?? []) as MatchingSourceFormValue[];

            if (!sourceRows.length) {
              return context.createError({ message: translate('rules.matching.validation.sourcesRequired') });
            }

            return sourceRows.every(row => !matchingSourceRowError(row, translate))
              ? true
              : context.createError({ message: translate('rules.matching.validation.sourceInvalid') });
          },
        }),
        definitions: yup.object().test({
          name: 'matchingDefinitions',
          test(value, context) {
            const values = context.parent as FormValues;
            const definitions = (value ?? {}) as MatchingDefinitions;

            const requiredKeys: (keyof MatchingDefinitions)[] =
              values.kind === MatchingRuleKind.exclusion
                ? ['excluded']
                : values.kind === MatchingRuleKind.blocking
                ? ['blocking']
                : ['match', 'partial', 'mismatch'];

            return requiredKeys.every(key => !!definitions[key]?.trim())
              ? true
              : context.createError({ message: translate('rules.matching.validation.definitionsRequired') });
          },
        }),
      }),
    [t],
  );

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const isExclusion = values.kind === MatchingRuleKind.exclusion;
    const isExact = values.type === RuleType.exact;

    const sources = values.sources.map(toMatchingSourceInput).filter((s): s is NonNullable<typeof s> => !!s);
    const grades = isExact
      ? values.grades
          .map(group => toMatchingGradeGroupInput(group, fieldsByEntityAndPath))
          .filter((g): g is NonNullable<typeof g> => !!g)
      : undefined;

    const definitions: MatchingDefinitions =
      values.kind === MatchingRuleKind.exclusion
        ? { excluded: values.definitions.excluded, notExcluded: values.definitions.notExcluded || undefined }
        : values.kind === MatchingRuleKind.blocking
        ? { blocking: values.definitions.blocking, clear: values.definitions.clear || undefined }
        : {
            match: values.definitions.match,
            partial: values.definitions.partial,
            mismatch: values.definitions.mismatch,
          };

    const input: MatchingRuleUpsertInput = {
      ...(rule ? { id: rule.id } : {}),
      ruleSetVersionId,
      kind: values.kind,
      type: values.type,
      name: values.name.trim(),
      ruleText: values.ruleText.trim(),
      enabled: values.enabled,
      ...(isExclusion ? {} : { weight: Number(values.weight), pairsWith: values.pairsWith || undefined }),
      sources,
      definitions,
      ...(isExact ? { grades } : {}),
    };

    try {
      await upsertRule({ variables: { input } });
      toastSuccess(t('rules.matching.rule.savedMessage'));
      await onSaved();
      onClose();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
      helpers.setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      disableBackdropClose
      title={rule ? t('rules.matching.rule.editTitle', { ruleId: rule.ruleId }) : t('rules.matching.rule.newTitle')}
    >
      <Formik
        key={rule?.id ?? 'new'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, setFieldValue }) => {
          const isExclusion = values.kind === MatchingRuleKind.exclusion;
          const isExact = values.type === RuleType.exact;
          const otherKind = OTHER_KIND[values.kind];
          const pairOptions = otherKind ? pairsWithOptions.filter(candidate => candidate.kind === otherKind) : [];
          const gradeOptions = MATCHING_GRADE_VALUES[values.kind];

          const replaceGrades = (next: MatchingGradeGroupFormValue[]) => setFieldValue('grades', next);

          return (
            <Form noValidate>
              <ResetOnKindChange />

              {!!rule?.note && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {rule.note}
                </Typography>
              )}

              <Text name="name" label={t('rules.matching.rule.field.name.label')} sx={{ mb: 2 }} />
              <Text name="ruleText" label={t('rules.editor.field.ruleText.label')} multiline rows={2} sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                  <Text
                    select
                    name="kind"
                    label={t('rules.matching.rule.field.kind.label')}
                    options={MATCHING_RULE_KIND_VALUES.map(value => ({
                      id: value,
                      name: t(`rules.matching.kind.option.${value}`) ?? value,
                    }))}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 160 } }}>
                  <Text
                    select
                    name="type"
                    label={t('rules.editor.field.type.label')}
                    options={MATCHING_RULE_TYPE_VALUES.map(value => ({
                      id: value,
                      name: t(`rules.type.option.${value}`) ?? '',
                    }))}
                  />
                </Box>

                {!isExclusion && (
                  <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                    <Text name="weight" label={t('rules.matching.rule.field.weight.label')} />
                  </Box>
                )}

                <Checkbox name="enabled" label={t('rules.editor.field.enabled.label')} />
              </Box>

              {!isExclusion && (
                <Box sx={{ width: { xs: '100%', sm: 320 }, mb: 2 }}>
                  <Text
                    select
                    name="pairsWith"
                    label={t('rules.matching.rule.field.pairsWith.label')}
                    helperText={t('rules.matching.rule.field.pairsWith.helperText')}
                    options={[
                      { id: '', name: t('rules.matching.rule.field.pairsWith.none') ?? '' },
                      ...pairOptions.map(candidate => ({
                        id: candidate.ruleId,
                        name: `${candidate.ruleId} — ${candidate.name}`,
                      })),
                    ]}
                  />
                </Box>
              )}

              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('rules.matching.source.sectionTitle')}
              </Typography>
              <MatchingSourcePicker name="sources" fields={fields} disabled={false} />

              <Typography variant="h4" sx={{ mt: 3, mb: 1 }}>
                {t('rules.matching.definitions.sectionTitle')}
              </Typography>

              {values.kind === MatchingRuleKind.exclusion && (
                <>
                  <Text
                    name="definitions.excluded"
                    label={t('rules.matching.definitions.field.excluded')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                  <Text
                    name="definitions.notExcluded"
                    label={t('rules.matching.definitions.field.notExcluded')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {values.kind === MatchingRuleKind.blocking && (
                <>
                  <Text
                    name="definitions.blocking"
                    label={t('rules.matching.definitions.field.blocking')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                  <Text
                    name="definitions.clear"
                    label={t('rules.matching.definitions.field.clear')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {values.kind === MatchingRuleKind.compatibility && (
                <>
                  <Text
                    name="definitions.match"
                    label={t('rules.matching.definitions.field.match')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                  <Text
                    name="definitions.partial"
                    label={t('rules.matching.definitions.field.partial')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                  <Text
                    name="definitions.mismatch"
                    label={t('rules.matching.definitions.field.mismatch')}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {isExact && (
                <>
                  <Typography variant="h4" sx={{ mt: 3, mb: 1 }}>
                    {t('rules.matching.grades.sectionTitle')}
                  </Typography>

                  {!values.grades.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('rules.matching.grades.empty')}
                    </Typography>
                  )}

                  {values.grades.map((group, groupIndex) => {
                    const groupName = `grades.${groupIndex}`;
                    const replaceConditions = (next: typeof group.conditions) =>
                      setFieldValue(`${groupName}.conditions`, next);
                    const { conditions } = group;

                    return (
                      <Box
                        key={group.uid}
                        sx={{ border: '1px solid', borderColor: 'grey.400', borderRadius: '16px', p: 2, mb: 2 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1">
                            {t('rules.matching.grades.groupHeading', { number: groupIndex + 1 })}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={values.grades.length <= 1}
                            onClick={() => replaceGrades(values.grades.filter((_g, i) => i !== groupIndex))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                            <Text
                              select
                              name={`${groupName}.grade`}
                              label={t('rules.matching.grades.field.grade')}
                              options={gradeOptions.map(value => ({
                                id: value,
                                name: t(`rules.matching.grade.option.${value}`) ?? value,
                              }))}
                            />
                          </Box>
                          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                            <Text
                              select
                              name={`${groupName}.logic`}
                              label={t('rules.editor.field.logic.label')}
                              options={LOGIC_VALUES.map(value => ({
                                id: value,
                                name: t(`rules.logic.option.${value}`) ?? '',
                              }))}
                            />
                          </Box>
                        </Box>

                        {conditions.map((_condition, conditionIndex) => (
                          <MatchingConditionRow
                            key={conditions[conditionIndex].uid}
                            name={`${groupName}.conditions.${conditionIndex}`}
                            index={conditionIndex}
                            fields={fields}
                            fieldsByEntityAndPath={fieldsByEntityAndPath}
                            disabled={false}
                            canRemove={conditions.length > 1}
                            canMoveUp={conditionIndex > 0}
                            canMoveDown={conditionIndex < conditions.length - 1}
                            onRemove={() => replaceConditions(conditions.filter((_c, i) => i !== conditionIndex))}
                            onMove={direction => {
                              const target = conditionIndex + direction;

                              if (target < 0 || target >= conditions.length) {
                                return;
                              }

                              const next = [...conditions];

                              [next[conditionIndex], next[target]] = [next[target], next[conditionIndex]];
                              replaceConditions(next);
                            }}
                          />
                        ))}

                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => replaceConditions([...conditions, emptyMatchingConditionRow()])}
                        >
                          {t('rules.editor.condition.addButton')}
                        </Button>
                      </Box>
                    );
                  })}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => replaceGrades([...values.grades, emptyMatchingGradeGroupRow(RuleLogic.all)])}
                  >
                    {t('rules.matching.grades.addGroupButton')}
                  </Button>
                </>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={onClose}>{t('rules.editor.cancelButton')}</Button>
                <LoadingButton loading={isSubmitting || loading} variant="contained" color="primary" type="submit">
                  {t('rules.colorCoding.saveButton')}
                </LoadingButton>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
